/**
 * The exercise registry.
 *
 * This is the "nothing else changes" half of build prompt §1. Every consumer —
 * prescription builder, session runner, safety monitor, charts, the server's
 * validation layer — asks the registry, never a hardcoded list.
 *
 * RULE FOR EXERCISE AUTHORS
 * -------------------------
 * An exercise file must not touch the DOM, `window`, or `document` at module
 * load time. Only inside `run()`. The server imports this registry to validate
 * parameters and read decay signatures, and there is no DOM there. Breaking
 * this rule crashes the API, not the browser, which is a confusing failure.
 */
const modules = new Map();
/**
 * The one registration line. Called once per exercise from index.ts.
 *
 * @throws if two exercises claim the same id — that would silently merge two
 *         different exercises' result histories, which is unrecoverable.
 */
export function register(module) {
    const { id } = module.definition;
    if (!/^[a-z][a-z0-9_]*$/.test(id)) {
        throw new Error(`Exercise id "${id}" must be lowercase snake_case (e.g. "smooth_pursuit").`);
    }
    if (modules.has(id)) {
        throw new Error(`Duplicate exercise id "${id}". Ids are permanent and appear in every ` +
            `stored result — reusing one would merge two exercises' histories.`);
    }
    if (module.definition.metrics.length === 0) {
        throw new Error(`Exercise "${id}" declares no metrics; charts would have nothing to plot.`);
    }
    // An exercise must either say how fatigue shows up in it, or say why it
    // cannot be watched. Both are decisions; a blank is not. The failure mode
    // this guards is silence — an exercise that quietly stopped being monitored
    // looks exactly like one that never needed to be.
    if (module.definition.decay.length === 0 && !module.definition.decayNotMonitored) {
        throw new Error(`Exercise "${id}" declares no decay signature and gives no reason. The EMC ` +
            `monitor (§9 Layer 3) cannot watch it, so say so: set "decayNotMonitored" ` +
            `to why, or declare how fatigue shows up in it.`);
    }
    if (module.definition.decay.length > 0 && module.definition.decayNotMonitored) {
        throw new Error(`Exercise "${id}" declares a decay signature AND says it is not monitored. ` +
            `It is one or the other.`);
    }
    for (const signal of module.definition.decay) {
        if (!module.definition.metrics.includes(signal.metric)) {
            throw new Error(`Exercise "${id}" declares decay on "${signal.metric}", which it does not emit.`);
        }
    }
    /**
     * The declared duration parameter must exist and be a number in seconds.
     * Checked here rather than trusted, because the thing that reads it is the
     * dose reduction (§9 Layer 5) and its failure mode is silence: a dose that
     * quietly stops being reduced after a safety event looks exactly like a dose
     * that never needed reducing.
     */
    const durationSpec = module.definition.params[module.definition.durationParam];
    if (!durationSpec) {
        throw new Error(`Exercise "${id}" names "${module.definition.durationParam}" as its duration ` +
            `parameter, but declares no such parameter. The safety system reduces a ` +
            `dose by shortening it and would silently do nothing.`);
    }
    if (durationSpec.kind !== 'number') {
        throw new Error(`Exercise "${id}" names "${module.definition.durationParam}" as its duration ` +
            `parameter, but it is a ${durationSpec.kind} parameter, not a number.`);
    }
    if (module.definition.allowedPositions.length === 0) {
        throw new Error(`Exercise "${id}" must allow at least one position.`);
    }
    // No check on allowedInputModes: EMPTY is meaningful and means the exercise
    // asks the patient for nothing — they watch. The oculomotor exercises all
    // became this on 2026-08-23. See the contract note on `allowedInputModes`.
    modules.set(id, module);
}
/** Every registered exercise, including retired ones. */
export function all() {
    return [...modules.values()];
}
/**
 * Exercises the doctor may prescribe today — retired ones are excluded from
 * the builder but stay registered so historical results still resolve
 * (constraint 9: nothing is ever deleted).
 */
export function available() {
    return all()
        .map((m) => m.definition)
        .filter((d) => !d.retiredOn)
        .sort((a, b) => a.name.localeCompare(b.name));
}
/** Grouped for the builder's category sections (§7). */
export function availableByCategory() {
    const grouped = {
        oculomotor: [],
        cognitive: [],
        'dual-task': [],
    };
    for (const definition of available()) {
        grouped[definition.category].push(definition);
    }
    return grouped;
}
/**
 * Look up a definition, including retired exercises — a stored result from a
 * retired exercise must still render on a chart.
 */
export function getDefinition(id) {
    return modules.get(id)?.definition;
}
/** Same, but throws. Use where a missing exercise means corrupt data. */
export function requireDefinition(id) {
    const definition = getDefinition(id);
    if (!definition) {
        throw new Error(`Unknown exercise id "${id}". Registered ids: ${[...modules.keys()].join(', ') || '(none)'}`);
    }
    return definition;
}
/**
 * The runtime half. Browser only — the server must never call this.
 */
export function getRunner(id) {
    const module = modules.get(id);
    if (!module)
        throw new Error(`Unknown exercise id "${id}".`);
    return module.run;
}
/** Test seam only. Never call from application code. */
export function __resetRegistryForTests() {
    modules.clear();
}
//# sourceMappingURL=registry.js.map